import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToolReadiness } from '../hooks/useToolReadiness';
import { AlertCircle, CheckCircle, Loader2, ExternalLink } from 'lucide-react';

interface ToolGateProps {
  toolKey: string;
  children: ReactNode;
  loadingMessage?: string;
  gateName?: string;
}

/**
 * ToolGate - Manages access to satellite tools with progressive activation flow
 * 
 * Flow: Anonymous → Sign In → Tenant Selection → GitHub App Install → Repo Access → Ready
 * 
 * States:
 * - Loading: Checking prerequisites
 * - Not Authenticated: Show sign-in prompt
 * - No Tenant: Show tenant creation/selection prompt
 * - No GitHub: Show GitHub App install prompt
 * - No Repo Access: Show repository configuration prompt
 * - Ready: Render children (the tool)
 */
export function ToolGate({ toolKey, children, loadingMessage, gateName }: ToolGateProps) {
  const navigate = useNavigate();
  const { data: readiness, isLoading, error } = useToolReadiness(toolKey);
  const returnToKey = 'return_to';

  useEffect(() => {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    if (returnTo.startsWith('/tools/')) {
      sessionStorage.setItem(returnToKey, returnTo);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center bg-slate-900/40 border border-slate-800 rounded-lg py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">{loadingMessage || 'Checking prerequisites...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-2xl w-full bg-slate-800 border border-slate-700 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Error Loading Tool</h3>
              <p className="text-slate-400 mb-4">
                Failed to check tool prerequisites. Please try again.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!readiness) {
    return null;
  }

  // Tool is ready - render children
  if (readiness.isReady) {
    return <>{children}</>;
  }

  // Not ready - show activation flow
  const primaryAction = readiness.actions.find(a => a.isPrimary);
  const secondaryActions = readiness.actions.filter(a => !a.isPrimary);

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-[1140px] mx-auto bg-slate-800/60 border border-slate-700/60 rounded-xl p-6 shadow-xl">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">
            {gateName || 'Tool'} Activation Required
          </h2>
          <p className="text-slate-400">
            Complete the following steps to activate this tool.
          </p>
        </div>

        {/* Progress Checklist */}
        <div className="space-y-3 mb-8">
          <ProgressItem
            label="Sign in to your account"
            completed={readiness.isAuthenticated}
          />
          <ProgressItem
            label="Select or create a tenant"
            completed={readiness.hasTenant}
            disabled={!readiness.isAuthenticated}
          />
          <ProgressItem
            label="Connect GitHub App"
            completed={readiness.hasGitHubInstallation}
            disabled={!readiness.hasTenant}
          />
          <ProgressItem
            label="Configure repository access"
            completed={readiness.hasRepoAccess}
            disabled={!readiness.hasGitHubInstallation}
          />
        </div>

        {/* Action Buttons */}
        {primaryAction && (
          <div className="space-y-3">
            <ActionButton
              action={primaryAction}
              isPrimary
              onClick={() => {
                if (primaryAction.url.startsWith('http')) {
                  window.location.href = primaryAction.url;
                } else {
                  navigate(primaryAction.url);
                }
              }}
            />

            {secondaryActions.map((action) => (
              <ActionButton
                key={action.key}
                action={action}
                isPrimary={false}
                onClick={() => {
                  if (action.url.startsWith('http')) {
                    window.location.href = action.url;
                  } else {
                    navigate(action.url);
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface ProgressItemProps {
  label: string;
  completed: boolean;
  disabled?: boolean;
}

function ProgressItem({ label, completed, disabled }: ProgressItemProps) {
  return (
    <div className={`flex items-center gap-3 ${disabled ? 'opacity-50' : ''}`}>
      {completed ? (
        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
      ) : (
        <div className="w-5 h-5 rounded-full border-2 border-slate-600 flex-shrink-0" />
      )}
      <span className={`${completed ? 'text-slate-300' : 'text-slate-400'}`}>
        {label}
      </span>
    </div>
  );
}

interface ActionButtonProps {
  action: { key: string; label: string; url: string };
  isPrimary: boolean;
  onClick: () => void;
}

function ActionButton({ action, isPrimary, onClick }: ActionButtonProps) {
  const isExternal = action.url.startsWith('http');

  return (
    <button
      onClick={onClick}
      className={`
        w-full px-6 py-3 rounded-md font-medium transition-colors flex items-center justify-center gap-2
        ${isPrimary
          ? 'bg-blue-600 hover:bg-blue-700 text-white'
          : 'bg-slate-700 hover:bg-slate-600 text-slate-200'
        }
      `}
    >
      {action.label}
      {isExternal && <ExternalLink className="w-4 h-4" />}
    </button>
  );
}
